import apiClient from "./client";
import { SubscriptionRequired } from "./errors";
import { bucketLabel } from "@/lib/bucketLabels";
import { isInflow } from "@/lib/transactionTotals";
import { FLEXIBLE_SPENDING_TARGET_COPY, accountCategoryOf, shortApiDate } from "@fomofi/shared";
import type { BankAccount } from "./banking";
import type { ApiTransaction } from "./transactions";
import type {
  AccountSummary,
  AlertCard,
  PayPeriodInfo,
  RecentTransaction,
  SpendingBucket,
} from "@/data/mockDashboard";

/* ─────────────────────────── API response types ───────────────────────────
 * Mirrors backend/apps/dashboard/serializers.py. All monetary/percentage values
 * are serialized as strings (DRF CharField over Decimal). */

export type AlertSeverity = "destructive" | "warning" | "info";

export interface DashboardAlert {
  /** Stable per-alert handle from the backend, unique within one response.
   *  Optional only so a client can still render against a backend older than
   *  the #806 fix; see {@link toAlertCards}. */
  id?: string;
  type: string;
  severity: AlertSeverity;
  title: string;
  message: string;
  date?: string | null;
  category?: string | null;
}

/** Per-parent PLANNED vs SPENT for the current pay period — the per-period basis
 *  behind the pay-period-titled cards (donut, Spending This Period, Plan at a
 *  Glance). Values are Decimal-as-string, like the rest of this payload. */
export interface PeriodBreakdown {
  start_date: string;
  end_date: string;
  by_parent: Record<"needs" | "wants" | "savings", { planned: string; spent: string }>;
  total_planned: string;
  total_spent: string;
  /** FP-176: the part of total_spent that needs a category. Optional for an older API. */
  uncategorized_spent?: string;
}

export interface DashboardRemainder {
  current_pay_period: { start_date: string; end_date: string };
  /** FP-062: false when no paycheck is detected (the window is the calendar month).
   *  Optional for a client deployed ahead of the API. */
  pay_period_detected?: boolean;
  total_income: string;
  total_expenses: string;
  remainder: string;
  /** FP-176: the part of total_expenses that needs a category (already inside it).
   *  Optional for an older API. */
  uncategorized_spent?: string;
  /* `suggested_allocation` was declared here and read by exactly one surface, the
   * donut's "Splash Fund from surplus". Removed with the backend field — see
   * DashboardService.get_remainder for why, and Insights' `found` for the one
   * place that derivation still legitimately ships. */
  /* `monthly_income` was declared here and never read. Removed with the backend
   * field in #904 — a forward-looking projection must not sit on the payload of a
   * screen whose percentages are trailing-12-month, under a name that invites use
   * as their denominator. */
  period_breakdown: PeriodBreakdown;
  /** Splash Fund for the current pay period. `spent` is NON-RECURRING Wants
   *  (contract batch 3A, WB D21), so it is no longer the Flexible-spending bar's
   *  figure. Optional for backward compat with older backends. */
  splash?: { planned: string; spent: string };
  /** "Bills covered" (#11), contract batch 3E: `planned` = bills due this period,
   *  `spent` = the part already paid. Optional for backward compat. */
  bills?: { planned: string; spent: string };
  /** Obligation-based debt payment for the current pay period: `planned` =
   *  minimums due in-period + planned extra, `spent` = minimums already past
   *  their due date. Optional for backward compat with older backends. */
  /** FP-037: `reason` "no_debts" when the user has no active debt; the bar then
   *  says so instead of planning a payment. */
  debt?: { planned: string; spent: string; reason?: "no_debts" | null };
  /** What the PREVIOUS pay period was left with, read back from its snapshot
   *  (DASH-19, BEN-141 P31). Null when the user has no closed period yet, and
   *  absent on a backend that predates the snapshot — both mean "nothing to
   *  carry in", and the card shows nothing rather than a fabricated $0.00.
   *  `is_final` is false while late transactions can still move the figure. */
  rollover?: {
    amount: string;
    start_date: string;
    end_date: string;
    is_final: boolean;
  } | null;
  /** "Available to spend" — cash in TRANSACTIONAL depository accounts, from the
   *  backend's canonical `banking.services.spendable_balance`.
   *
   *  Label it with those exact words. It is NOT "Total deposits" (all depository,
   *  savings included) and NOT "Total net worth" (assets − liabilities), and no
   *  page may print two figures both called "balance" (BEN-141 D2). The dashboard
   *  used to reduce account rows itself and got $14,000 where the paycheck card
   *  on the same page said $4,200.
   *
   *  Optional for backward compat with older backends; null renders the card's
   *  honest empty state rather than a client-side substitute. */
  spendable_balance?: string;
  /** THE bills-due figure, from forecasting.bills_due_before_next_pay — the
   *  same number the Forecast page prints. Optional for backward compat. */
  bills_due_before_next_pay?: string;
  /** Whether an income stream puts a paycheck ahead of today — the predicate
   *  the Forecast answers with `next_paycheck !== null`. The bills-due label
   *  names a paycheck only when this is true (#1251). Optional for backward
   *  compat; absent keeps the paycheck wording. */
  has_next_paycheck?: boolean;
  /** THE day countdown, from forecast_plan.days_left_in_period. Exclusive of
   *  today and coherent with this payload's own period dates. */
  days_left?: number;
  /** R5: the newest transaction the account holds (ISO), null with none. */
  data_through?: string | null;
  /** R5: the window starts after `data_through` and no bank has synced since.
   *  Optional for a client deployed ahead of the API (absent = not stale). */
  data_stale?: boolean;
  /** R5: which stale state. True = a bank needs reconnecting (A), else B. */
  stale_reconnect_needed?: boolean;
}

/* ─────────────────────────────── fetchers ─────────────────────────────── */

export async function getDashboardAlerts(): Promise<DashboardAlert[]> {
  try {
    const { data } = await apiClient.get<DashboardAlert[]>("/dashboard/alerts/");
    return data;
  } catch (error) {
    // FP-226 (paywall rehearsal, 2026-10-15): alerts are a paid endpoint, so an unpaid
    // account gets the paywall's 402 and the bell read "Couldn't load notifications".
    // For that account there is nothing to notify about, not a failure. Mobile: FP-203.
    if (error instanceof SubscriptionRequired) return [];
    throw error;
  }
}

export async function getDashboardRemainder(): Promise<DashboardRemainder> {
  const { data } = await apiClient.get<DashboardRemainder>("/dashboard/remainder/");
  return data;
}

/* ───────────────────── adapters: snake_case API → UI types ─────────────────
 * Kept in the API layer (not inline in components) so the wiring is testable
 * and components stay presentational. */

const num = (v: string | number | null | undefined): number => Number(v) || 0;

/** Spending buckets — PER PAY PERIOD (the card is titled "Spending This Pay
 *  Period"). Needs/Wants/Savings planned-vs-spent come from the remainder's
 *  period breakdown (monthly budget prorated to the period + actuals summed over
 *  the period window), so they reconcile with the pay-period donut and the
 *  paycheck page — not whole-month budget/actual (#466/#468/#474).
 *
 *  Splash/Debt read `remainder.splash` / `remainder.debt` — the SAME fields
 *  `toPayPeriodInfo` feeds into the donut's bars, on the same pay-period window.
 *  They used to be hardcoded `0, 0` from when the backend genuinely had no
 *  per-period field; the backend added both (apps/dashboard/services.py
 *  get_remainder) and only `toPayPeriodInfo` was updated, so the donut showed
 *  "Splash Fund used $244 of $56" while this card showed "$0 / $0, 0% used" for
 *  the same user in the same period (BEN-141 Part 2).
 *
 *  Rollover reads `remainder.rollover`, the previous pay period's recorded
 *  remainder (DASH-19 / #384, BEN-141 P31). It was the last of the three still
 *  hardcoded 0: nothing wrote down what a period closed with, so there was no
 *  honest figure to show. `toRollover` carries the dates so the card can name
 *  the period it came from — "$1,200" with no window is not a fact. */
/** A #9 bucket's spend this period, named for its window (#1392). The Splash
 *  card keeps its name: its numerator is 3A's to change. */
const spentLabel = (key: string, window: string) => `${bucketLabel(key)} spent ${window}`;

export function toSpendingBuckets(remainder: DashboardRemainder | undefined): SpendingBucket[] {
  if (!remainder) return [];
  const bp = remainder.period_breakdown.by_parent;
  // FP-062: no detected paycheck, so the window is the calendar month.
  const window = remainder.pay_period_detected === false ? "this month" : "this period";
  const rows: Array<[string, string, number, number, string]> = [
    ["needs", spentLabel("needs", window), num(bp.needs.spent), num(bp.needs.planned), "rose"],
    ["wants", spentLabel("wants", window), num(bp.wants.spent), num(bp.wants.planned), "orange"],
    [
      "savings",
      spentLabel("savings", window),
      num(bp.savings.spent),
      num(bp.savings.planned),
      "emerald",
    ],
    [
      "splash-fund",
      // Rule 2 (2026-10-14): `remainder.splash` is not Forecast's Splash Fund bucket.
      FLEXIBLE_SPENDING_TARGET_COPY,
      num(remainder.splash?.spent),
      num(remainder.splash?.planned),
      "sky",
    ],
    ["debt", bucketLabel("debt"), num(remainder.debt?.spent), num(remainder.debt?.planned), "red"],
    // Rollover has no plan, so `budgeted` stays 0 and the card renders `spent` as
    // the carried-in figure (see SpendingThisPeriod's isRollover branch).
    ["rollover", "Rollover", num(remainder.rollover?.amount), 0, "slate"],
  ];
  const carried = remainder.rollover;
  const note = carried
    ? `Carried in from ${shortApiDate(carried.start_date)} – ${shortApiDate(carried.end_date)}` +
      (carried.is_final ? "" : ", still settling")
    : undefined;
  return rows.map(([id, label, spent, budgeted, color]) => ({
    id,
    label,
    spent,
    budgeted,
    percentUsed: budgeted > 0 ? Math.round((spent / budgeted) * 100) : 0,
    color,
    note: id === "rollover" ? note : undefined,
  }));
}

/** Current pay-period summary (the donut). PLANNED = this period's prorated
 *  budget, SPENT = this period's actual expenses (period_breakdown) — so the
 *  "Spent $X of $Y" gauge is a true per-period figure that reconciles with the
 *  remainder card's period expenses and the paycheck page, not whole-month
 *  budget/actual under a per-period title (#466/#468/#474). */
export function toPayPeriodInfo(remainder: DashboardRemainder | undefined): PayPeriodInfo | null {
  if (!remainder) return null;
  // BEN-141 4b (4a D8). READ, do not derive. This was
  // `Math.round((end - today) / msPerDay)`, which had TWO independent faults:
  // `new Date("2026-08-09")` parses as UTC midnight while `new Date()` is local, and
  // rounding a fractional day span flips between floor and ceil by hour of day. It
  // rendered "(4 days left)" against a period ending Aug 9 where the Forecast
  // correctly said 5. The backend figure is exclusive of today and coherent with the
  // period dates printed beside it.
  const daysLeft = remainder.days_left ?? 0;
  // BEN-141 N19 (#1159). The two ENDS of that same period kept the fault the day
  // count shed: they were rendered here with `new Date(iso).toLocaleDateString()`,
  // a UTC-midnight parse read back in the device's zone, so the dashboard printed
  // "Sep 2 – Sep 15" beside a Forecast reading "Sep 3 – Sep 16" for the one period
  // the server sent them both, and beside its own correct "9 days left". Any
  // negative-offset zone loses the day; UTC does not, which is why it survived CI.
  // `shortApiDate` is the module both clients already share for exactly this
  // (#744), and it fixes the locale as well, so a non-US device cannot reorder one
  // surface's dates and not the other's.
  const { start_date: start, end_date: end } = remainder.current_pay_period;

  const totalPlanned = num(remainder.period_breakdown.total_planned);
  const totalSpent = num(remainder.period_breakdown.total_spent);
  const bp = remainder.period_breakdown.by_parent;

  return {
    currentPeriodLabel: `${shortApiDate(start)} – ${shortApiDate(end)}`,
    daysLeft,
    detected: remainder.pay_period_detected ?? true,
    totalPlanned,
    totalSpent,
    uncategorizedSpent: num(remainder.period_breakdown.uncategorized_spent),
    percentUsed: totalPlanned > 0 ? Math.round((totalSpent / totalPlanned) * 100) : 0,
    // SIGNED. Overspending the plan is the single most important thing this card
    // can tell a user, and `Math.max(0, …)` deleted exactly that: uid 266 spent
    // $1,820 against a $1,650 plan and the card printed "Remaining $0" — the same
    // string a user who has spent precisely to plan sees. The −$170 was floored
    // away while the badge two rows up already said "Over Budget" (BEN-141 2e).
    remaining: totalPlanned - totalSpent,
    // BEN-141 — finishing F5. `splashFundAvailable` was
    // `suggested_allocation.splash_fund`: the period surplus × (wants_pct ÷ 2).
    // F5 moved the Splash tile off that quantity because a slice of a balance
    // cannot be the denominator for wants SPENDING, and rewired it onto
    // `remainder.splash` — the period's wants allocation. The donut was left
    // behind, so one page carried two figures named Splash Fund ($60 here
    // against $630 on Plan at a Glance, uid 270). The previous batch's answer
    // was to distinguish them by name ("Splash Fund from surplus"); this one
    // removes the figure, because the ÷ 2 has no recorded derivation anywhere
    // in the repo or the specs — no ticket, no commit message, no comment
    // states why half. The bar below (`bars.splash`) already renders the
    // per-period Splash Fund the rest of the page uses.
    //
    // `splashFundRollover` goes with it: it was hardcoded null (no backend
    // source has ever existed) and it only ever annotated the removed figure.
    // #11 field mappings (FLAGGED for Anthony — see PR):
    // "Safe to spend" = the remainder endpoint's canonical figure (period
    // income − period expenses). "Projected leftover" = what's left of the
    // period PLAN (planned − spent). "Bills due before next paycheck" is
    // DERIVED as unpaid planned Bills & Essentials (needs planned − spent) —
    // no dedicated backend field exists yet.
    safeToSpend: num(remainder.remainder),
    // Signed, for the reason above — this is the same figure as `remaining`, so
    // flooring one and not the other would put two different numbers for one
    // quantity on one card.
    projectedLeftover: totalPlanned - totalSpent,
    // BEN-141 4b 1b. READ, do not derive. This was
    // `Math.max(0, needsPlanned - needsSpent)` — the UNSPENT NEEDS BUDGET, which is
    // not bills at all: it read $481 on uid 270 while the Forecast, summing the
    // actual dated obligations, read $165.49 for the same user in the same session.
    // A budget remainder and a sum of dated bills are different quantities and no
    // labelling could reconcile them. Now both surfaces read one backend figure.
    billsDueBeforeNextPaycheck:
      remainder.bills_due_before_next_pay !== undefined
        ? num(remainder.bills_due_before_next_pay)
        : null,
    // #1251. The figure is bills inside the window either way; only the label
    // named a paycheck, which an account with no income stream does not have.
    // The Forecast's counterpart (#1249) says "In This Window" for that case.
    billsDueLabel:
      remainder.has_next_paycheck === false
        ? "Bills due in this window"
        : "Bills due before next paycheck",
    // "Available to spend", straight from the payload. Previously the page
    // reduced checking+savings itself and labelled the result "Current balance";
    // that figure was both wrong (it swept in savings, because the frontend
    // account enum collapses Plaid's depository subtypes) and unlabelled as to
    // WHICH balance it was. Read, do not re-derive (BEN-141 D2(ii)).
    currentBalance:
      remainder.spendable_balance !== undefined ? num(remainder.spendable_balance) : null,
    // Per-bucket progress-bar inputs. Splash (wants-parent spend vs suggested
    // splash allocation) and Debt (obligation-based: minimums due vs paid) now
    // come from the remainder payload; null only when an older backend omits
    // them, which still renders the bars' honest "not tracked yet" state.
    bars: {
      // "Bills covered": bills paid of bills due (contract batch 3E). It was the
      // needs budget and needs spend, which are neither bills nor coverage, so an
      // older backend without the block shows "Not tracked yet" rather than that.
      needs: remainder.bills
        ? { spent: num(remainder.bills.spent), planned: num(remainder.bills.planned) }
        : null,
      wants: { spent: num(bp.wants.spent), planned: num(bp.wants.planned) },
      splash: remainder.splash
        ? { spent: num(remainder.splash.spent), planned: num(remainder.splash.planned) }
        : null,
      debt: remainder.debt
        ? {
            spent: num(remainder.debt.spent),
            planned: num(remainder.debt.planned),
            reason: remainder.debt.reason ?? null,
          }
        : null,
    },
    // R5: the card withholds its figures and says why, instead of printing $0.
    stale:
      remainder.data_stale && remainder.data_through
        ? { through: remainder.data_through, reconnect: remainder.stale_reconnect_needed === true }
        : null,
  };
}

const ALERT_TYPE_MAP: Record<string, AlertCard["type"]> = {
  spending_over_budget: "overage",
  bill_due: "large-bill",
  cashflow_warning: "cash-flow",
  // #713. Listed rather than left to the `?? "overage"` default below: a
  // fallback that lands on a real category cannot say "I do not recognise this",
  // which is the shape of #719 in a different file.
  stale_connection: "stale-connection",
};

/** Alerts → NotificationBell's AlertCard.
 *
 *  The id must be UNIQUE PER ALERT, not per alert kind. It is both the React
 *  list key and the handle `NotificationBell` dismisses by, so a collision means
 *  React cannot tell two rows apart and dismissing one dismisses every alert
 *  sharing the id (#806).
 *
 *  The backend now stamps that id (`DashboardService.assign_alert_ids`), so web
 *  and mobile dismiss by the same handle and neither has to guess at what makes
 *  two alerts different. It is derived from the alert's content rather than its
 *  position, which is what dismiss-by-id needs: a new alert arriving at the top
 *  of the list must not move everything else's handle and resurrect a
 *  dismissal.
 *
 *  The local fallback below is only reachable against a backend older than that
 *  change (a rollback). It keeps the pre-#806 derivation plus the title, which
 *  is what distinguishes siblings, and the index as a final tiebreak.
 */
export function toAlertCards(alerts: DashboardAlert[] | undefined): AlertCard[] {
  if (!alerts) return [];
  return alerts.map((a, i) => ({
    id: a.id ?? `${a.type}-${a.date ?? a.category ?? "none"}-${a.title}-${i}`,
    type: ALERT_TYPE_MAP[a.type] ?? "overage",
    title: a.title,
    message: a.message,
    severity: a.severity === "destructive" || a.severity === "info" ? a.severity : "warning",
  }));
}

/** Linked bank accounts (/banking/accounts/) → Accounts card rows.
 *  Liabilities (credit/loan) are SIGNED NEGATIVE so the card's net-worth sum is
 *  assets − liabilities — a user with debt never sees an inflated figure
 *  (#Flag 5). Plaid reports their balance as a positive magnitude of debt owed. */
export function toAccountSummaries(accounts: BankAccount[] | undefined): AccountSummary[] {
  if (!accounts) return [];
  return accounts.map((a) => {
    // The mask is a FIELD (`BankAccount.mask`, straight from Plaid), not something
    // to be recovered from a display name.
    //
    // BEN-141 2c: this read `official_name.match(/\d{4}$/)` and fell back to
    // `String(a.id).slice(-4)` — the account's PRIMARY KEY. Plaid's official
    // names ("Plaid Platinum Standard 0.5% Interest Credit Card") end in a word,
    // not four digits, so the regex missed and the fallback fired: uid 266's Visa
    // rendered "····0811" (row id 811) beside Financial Analysis's correct
    // "•••• 4444" for the same account. A digit-shaped lie is worse than no
    // digits, because it reads as a verifiable fact the user can check against
    // their card.
    //
    // Empty string when the account genuinely has no mask (manual/legacy rows) —
    // AccountsCard omits the "····" affix rather than inventing four characters.
    const mask = a.mask?.trim() ?? "";
    // Liabilities subtract their (signed) balance — mirrors the backend
    // `net_worth` helper exactly, so the card's sum is assets − liabilities.
    const balance = num(a.current_balance);
    // `type` is a DISPLAY category (it selects an icon). It is the server's
    // `account_category` when the payload carries one, and the same ladder run
    // locally when it does not — see `@fomofi/shared/utils/accountType`.
    //
    // BEN-141 2b: this once tested `a.account_type === "savings"`, which is dead
    // code — banking/services.py writes account_type from Plaid's
    // `acct.type.value`, whose depository value is the literal "depository";
    // checking vs savings lives in account_subtype. 2b fixed savings by adding a
    // subtype check but left the ladder ending in `: "checking"`, so every
    // `investment` account still landed on checking — 63 of them on the dev host,
    // including a $23,631.98 401k rendering the checking glyph (#719). A ladder
    // whose fallback is a real category cannot say "I do not know"; this one
    // ends at `other`.
    const type: AccountSummary["type"] = accountCategoryOf(
      a.account_category,
      a.account_type,
      a.account_subtype,
    );
    return {
      id: String(a.id),
      name: a.nickname || a.name || (a.official_name ?? ""),
      balance: a.is_liability ? -balance : balance,
      type,
      mask,
    };
  });
}

/** Recent transactions (/transactions/) → Recent Transactions card rows.
 *  "Recent" excludes future-dated rows (e.g. a bill deferred past today by the
 *  demo's pay-period balancing) — only transactions dated ≤ today qualify
 *  (#Flag 7). */
export function toRecentTransactions(
  transactions: ApiTransaction[] | undefined,
): RecentTransaction[] {
  if (!transactions) return [];
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  // Date-only strings parse as LOCAL midnight ("T00:00:00") — bare
  // new Date("YYYY-MM-DD") is UTC and shifts to the previous day in
  // negative-offset timezones (wrong dates + today's rows filtered out).
  return transactions
    .filter((t) => new Date(t.date + "T00:00:00").getTime() <= endOfToday.getTime())
    .slice(0, 5)
    .map((t) => {
      const merchant = t.merchant_name || t.name;
      const slug = merchant
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "")
        .trim();
      return {
        id: String(t.id),
        merchant,
        amount: Math.abs(num(t.amount)),
        type: t.is_income ? ("income" as const) : ("expense" as const),
        inflow: isInflow(t),
        date: new Date(t.date + "T00:00:00").toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
        category: t.category?.name ?? t.plaid_category_primary ?? "",
        logoUrl: slug ? `https://cdn.simpleicons.org/${slug}` : undefined,
      };
    });
}
